import { ReactNode, useState } from 'react'

/*
  Component structure
    - UploadButton x 1
    - UploadModal x 1
      - UploadLandingScreen x 1
      - UploadCSVScreen x 1
      - UploadReviewScreen x 1
      - UploadDuplicateScreen x 1
        - UploadDuplicateRow x N
*/

// UI
import Screens, { ScreenData } from './screens'
import { Button, Icon, Dialog, Dropdown } from '@/ui'
import {
  faSackDollar,
  faCreditCard,
  faCaretDown,
} from '@fortawesome/pro-solid-svg-icons'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { TransactionTypes } from '@/features/transactions/constants'
import { useAnalyticsTrack } from '@/features/amplitude'
import { UploadTransactionSheet } from './UploadTransactionSheet'
import { TextColors } from '@/styles'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

export interface UploadSuccessPayload {
  id?: number
  date: string
  description: string
  amountInDollars: string
}

interface UploadButtonProps {
  onUploadComplete: (_args: {
    success: boolean
    data?: UploadSuccessPayload[]
  }) => void
  render?: (props: { onClick: () => void }) => ReactNode
}

export const UploadButton = ({
  render,
  onUploadComplete,
}: UploadButtonProps) => {
  const [open, setOpen] = useState(false)
  const defaultScreenIndex = 1
  const [screenIndex, setScreenIndex] = useState(defaultScreenIndex)
  const [screenData, setScreenData] = useState<ScreenData>({})
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const [sheetIcon, setSheetIcon] = useState<IconProp | null>(null)
  const track = useAnalyticsTrack()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [sheetType, setSheetType] = useState<TransactionTypes | null>(null)

  const toggleAddTransactionsModal = () => {
    setOpen((prev) => !prev)
    setScreenIndex(defaultScreenIndex)
  }

  const goToScreenWithData = ({
    index,
    data,
  }: {
    index: number
    data?: ScreenData
  }) => {
    setScreenData(data || {})
    setScreenIndex(index)
  }

  const CurrentScreen = Screens[screenIndex]

  const openSheet = (type: TransactionTypes) => {
    const isIncomeTxn = type === TransactionTypes.INCOME
    const icon = isIncomeTxn ? faSackDollar : faCreditCard

    setSheetType(type)
    setSheetOpen(true)
    setSheetIcon(icon)
    track('clicked add transaction flyout button')
  }

  const closeSheet = () => {
    setSheetOpen(false)
    setSheetType(null)
  }

  return (
    <>
      {!render && (
        <Dropdown>
          <Dropdown.Trigger>
            <Button className="flex gap-4" size="md">
              <Icon name="plus" icon={faPlus} color={TextColors.WHITE} />
              {isMobile ? 'Add' : 'Add Transaction'}
              <Icon name="down" icon={faCaretDown} color={TextColors.WHITE} />
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Content className="w-full">
            <Dropdown.Item
              onClick={() => openSheet(TransactionTypes.INCOME)}
              className="w-full text-lg"
            >
              Add Income
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => openSheet(TransactionTypes.EXPENSE)}
              className="w-full text-lg"
            >
              Add Expense
            </Dropdown.Item>
            <Dropdown.Separator />
            <Dropdown.Item
              onClick={toggleAddTransactionsModal}
              className="w-full text-lg"
            >
              Add Multiple Transactions
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown>
      )}

      {render?.({ onClick: toggleAddTransactionsModal })}

      <Dialog open={open} onClose={() => setOpen(false)}>
        <Dialog.Title />
        <Dialog.Content className="max-h-[60vh] overflow-y-auto">
          <CurrentScreen
            data={screenData}
            onUploadComplete={onUploadComplete}
            finishFlow={() => setOpen(false)}
            goToScreenWithData={goToScreenWithData}
          />
        </Dialog.Content>
      </Dialog>

      <UploadTransactionSheet
        sheetOpen={sheetOpen}
        sheetIcon={sheetIcon}
        closeSheet={closeSheet}
        sheetType={sheetType || TransactionTypes.DEFAULT}
        onUploadComplete={onUploadComplete}
      />
    </>
  )
}
